// What the web host knows about itself, in the two shapes the diagnostics
// screen needs: the build header every copy carries, and the "snapshot" page.
//
// The native hosts show the brain's own snapshot (docs/09 §5), which they get
// over the bridge. The web app composes the brain in-process and has no such
// document, so this builds the equivalent from the facts the browser can state
// — and builds it from an ALLOW-LIST: every field below is a count, a flag, a
// closed vocabulary, or a hostname passed through `safeHost*`. Nothing here can
// carry a token, an address or a UUID, which matters because a snapshot is
// exactly the page a user photographs and forwards.

import { brainVersion } from '@merkury/brain/runtime/version.js';
import {
  containsResidue,
  safeCommit,
  safeHostFromUrl,
  safeVersion,
} from '@merkury/brain/runtime/diag/redact.js';
import type { DiagSlice } from '@merkury/brain/runtime/diag/index.js';
import type { BundleBuildInfo } from './bundle.js';

/**
 * Browser families we are willing to name, with the major version only. An
 * allow-list rather than a user-agent string: the UA is a fingerprint and most
 * of it answers no diagnostic question, while "Safari 18 vs Chrome 140" answers
 * several. Order matters — Chromium UAs also carry `Safari`, and Edge carries
 * both.
 */
const BROWSERS: readonly [string, RegExp][] = [
  ['Firefox', /\bFirefox\/(\d{1,4})/],
  ['Edge', /\bEdg(?:e|A|iOS)?\/(\d{1,4})/],
  ['Samsung', /\bSamsungBrowser\/(\d{1,4})/],
  ['Chrome', /\b(?:Chrome|CriOS)\/(\d{1,4})/],
  ['Safari', /\bVersion\/(\d{1,4})[.\d]*\s+(?:Mobile\/\S+\s+)?Safari\//],
];

/** OS families, named without their version — the version is the identifying
 *  half and answers far less than the family does. */
const PLATFORMS: readonly [string, RegExp][] = [
  ['Android', /\bAndroid\b/],
  ['iOS', /\b(?:iPhone|iPad|iPod)\b/],
  ['macOS', /\bMac OS X\b/],
  ['Windows', /\bWindows\b/],
  ['Linux', /\bLinux\b/],
];

/** `macOS · Chrome 140`, or undefined when nothing in the allow-list matched. */
export function coarseUserAgent(ua: string | undefined): string | undefined {
  if (typeof ua !== 'string' || ua === '') return undefined;
  const parts: string[] = [];
  for (const [name, re] of PLATFORMS) {
    if (re.test(ua)) {
      parts.push(name);
      break;
    }
  }
  for (const [name, re] of BROWSERS) {
    const major = re.exec(ua)?.[1];
    if (major !== undefined) {
      parts.push(`${name} ${major}`);
      break;
    }
  }
  return parts.length === 0 ? undefined : parts.join(' · ');
}

/**
 * The build header every copy carries. `VITE_APP_VERSION` / `VITE_APP_COMMIT`
 * are baked by `make frontend-build`, exactly as the Settings version footer
 * reads them, so a pasted report names the deploy it came from.
 */
export function webBuildInfo(): BundleBuildInfo {
  const env = import.meta.env as Record<string, string | undefined>;
  const brain = brainVersion();
  const version = env['VITE_APP_VERSION'] ?? (env['MODE'] as string | undefined) ?? 'dev';
  const commit = env['VITE_APP_COMMIT'];
  const ua = coarseUserAgent(
    (globalThis as { navigator?: { userAgent?: string } }).navigator?.userAgent,
  );
  return {
    appVersion: version,
    ...(commit === undefined ? {} : { appBuild: commit }),
    platform: 'web',
    ...(ua === undefined ? {} : { osVersion: ua }),
    brainVersion: brain.version,
    brainCommit: brain.commit,
    brainBuiltAt: brain.builtAt,
    brainContract: brain.contract,
    brainEntry: 'web',
  };
}

/** Everything the snapshot page states, before it is rendered. */
export interface WebSnapshotInput {
  atMs: number;
  build: BundleBuildInfo;
  /** The brain's own additive dev/prompt block (docs/09 §5) — counts and flags. */
  diag: DiagSlice;
  /** Where the next REST byte would go. Reduced to a host, never a path. */
  restBase?: string | undefined;
  endpoints?:
    | {
        version?: number | undefined;
        signaling?: number | undefined;
        turn?: number | undefined;
        doh?: number | undefined;
        diag?: number | undefined;
        lastGoodUrl?: string | undefined;
      }
    | undefined;
  online?: boolean | undefined;
  /** `navigator.connection.effectiveType`-style token, narrowed below. */
  networkKind?: string | undefined;
}

/** Connection tokens the browser is allowed to state. Allow-list. */
const NETWORK_KINDS: ReadonlySet<string> = new Set([
  'slow-2g',
  '2g',
  '3g',
  '4g',
  '5g',
  'wifi',
  'ethernet',
  'cellular',
  'bluetooth',
  'wimax',
  'other',
  'none',
  'unknown',
]);

function count(n: unknown): number {
  return typeof n === 'number' && Number.isFinite(n) && n > 0 ? Math.round(n) : 0;
}

/**
 * The snapshot page, as JSON. Every value comes out of a coercion above, so the
 * document's safety is a property of its constructors — but it is still put
 * through the §3 residue gate afterwards, because a snapshot that somehow
 * carried an id would be copied out by hand rather than caught by the uploader.
 * A tripped gate withholds the document rather than scrubbing it (docs/10 §9).
 */
export function webSnapshotJson(input: WebSnapshotInput): string {
  const ep = input.endpoints;
  const document = {
    at: Math.floor(input.atMs / 1000),
    platform: input.build.platform,
    app: {
      version: safeVersion(input.build.appVersion) ?? 'unknown',
      commit: safeCommit(input.build.appBuild) ?? null,
      ua: input.build.osVersion ?? null,
    },
    brain: {
      version: safeVersion(input.build.brainVersion) ?? 'unknown',
      commit: safeCommit(input.build.brainCommit) ?? null,
      contract: count(input.build.brainContract),
    },
    net: {
      online: input.online === true,
      kind:
        input.networkKind !== undefined && NETWORK_KINDS.has(input.networkKind)
          ? input.networkKind
          : 'unknown',
      restHost: safeHostFromUrl(input.restBase ?? ''),
    },
    endpoints:
      ep === undefined
        ? null
        : {
            version: count(ep.version),
            signaling: count(ep.signaling),
            turn: count(ep.turn),
            doh: count(ep.doh),
            diag: count(ep.diag),
            lastGoodHost: safeHostFromUrl(ep.lastGoodUrl ?? ''),
          },
    diag: {
      devMode: input.diag.devMode === true,
      autoSend: input.diag.autoSend === true,
      disclosurePending: input.diag.disclosure === true,
      queued: count(input.diag.queued),
      prompt: input.diag.prompt ?? null,
      lastOutcome: input.diag.lastOutcome ?? null,
    },
  };
  const json = JSON.stringify(document, null, 2);
  return containsResidue(json) ? '{ "withheld": "failed the redaction gate" }' : json;
}
